// Security utilities for the application

// Global flag to prevent multiple initializations
let securityInitialized = false;

/**
 * Initialize basic client-side security measures
 */
export function initializeSecurityMeasures() {
  // Prevent multiple initializations
  if (securityInitialized) {
    return;
  }
  
  securityInitialized = true;
  // Add stronger CSP via meta tag if not already set by server
  // Note: frame-ancestors is omitted as it's ignored in meta elements
  if (!document.querySelector('meta[http-equiv="Content-Security-Policy"]')) {
    const cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = [
      "default-src 'self'",
      "script-src 'self' https://fonts.googleapis.com https://maps.googleapis.com https://maps.gstatic.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com data:",
      "img-src 'self' data: blob: https: https://maps.googleapis.com https://maps.gstatic.com",
      "media-src 'self' data: blob: https://*.supabase.co",
      "connect-src 'self' https://*.supabase.co wss://*.supabase.co https://maps.googleapis.com",
      "object-src 'none'",
      "base-uri 'self'"
    ].join('; ');
    document.head.appendChild(cspMeta);
  }

  // Disable right-click context menu in production to prevent basic inspection
  if (process.env.NODE_ENV === 'production') {
    document.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // Add frame busting to prevent clickjacking (only in production, not in Lovable iframe)
  if (process.env.NODE_ENV === 'production' && window.top !== window.self) {
    try {
      // Only attempt if we can access window.top (not blocked by CORS)
      if (window.top?.location.hostname !== window.self.location.hostname) {
        window.top.location = window.self.location;
      }
    } catch (e) {
      // Silently ignore if we can't access parent frame (expected in development)
      console.log('Frame busting skipped (running in iframe)');
    }
  }

  console.log('🔒 Security measures initialized');
}

/**
 * Validate input to prevent basic XSS attacks
 */
export function sanitizeInput(input: string): string {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

/**
 * Validate URLs for safety
 */
export function isSecureUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    return ['https:', 'data:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
}

/**
 * Rate limiting for API calls (client-side)
 */
class RateLimiter {
  private requests: Map<string, number[]> = new Map();
  private limit: number;
  private windowMs: number;

  constructor(limit: number = 100, windowMs: number = 60000) {
    this.limit = limit;
    this.windowMs = windowMs;
  }

  canMakeRequest(key: string): boolean {
    const now = Date.now();
    const requests = this.requests.get(key) || [];
    
    // Remove old requests outside the window
    const validRequests = requests.filter(time => now - time < this.windowMs);
    
    if (validRequests.length >= this.limit) {
      return false;
    }

    validRequests.push(now);
    this.requests.set(key, validRequests);
    return true;
  }
}

export const defaultRateLimiter = new RateLimiter();